import { BaseQueryApi, createApi, FetchArgs, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import queryString from 'query-string';
import axios from 'axios';
import { store } from '../store';
import { actions } from '../ducks/auth';
import { toast } from 'react-toastify';
import { CustomErrorToast } from '@shared/components/customErrorToast';

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

const toastIgnoreList = [
  { uri: '/sip-trunk', status: [404] },
  { uri: '/reports', status: [400, 403, 404, 500] },
  { uri: '/agent-profile', status: [409] },
  { uri: 'sign-in', status: [400, 401, 404] },
  { uri: '/admin/companies/', status: [409] },
  { uri: '/companies/', status: [404, 409] },
];

async function refreshToken(url: string): Promise<string | null> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = (async () => {
      try {
        const refreshEndpoint = url.includes('super-admin')
          ? '/v1/admin/refresh-token'
          : '/v1/user/refresh-token';

        // refreshToken lives in an HttpOnly cookie - the browser attaches it automatically
        const refreshResponse = await axios.post(
          `${import.meta.env.VITE_API_URL}${refreshEndpoint}`,
          {},
          { withCredentials: true },
        );

        const { accessToken } = refreshResponse.data;

        if (url.includes('super-admin')) {
          store.dispatch(actions.signInSuperAdmin({ accessToken }));
        } else {
          store.dispatch(actions.signIn({ accessToken }));
        }

        return accessToken;
      } catch (error: unknown) {
        console.error('Error refreshing token:', error);
        toast.error('Session expired. Please log in again.');

        store.dispatch(actions.signOut());
        return null;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    })();
  }

  return refreshPromise;
}

export const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL,
  credentials: 'include',
  prepareHeaders: (headers) => {
    const url = window.location.href || '';
    const token = url.includes('super-admin')
      ? localStorage.getItem('superAdminAccessToken')
      : localStorage.getItem('accessToken');

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    return headers;
  },
  paramsSerializer: (params) => queryString.stringify(params),
});

export const baseQueryWithReauth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: object,
) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const url = window.location.href || '';
    const newAccessToken = await refreshToken(url);

    if (newAccessToken) {
      const newHeaders = new Headers();
      newHeaders.set('authorization', `Bearer ${newAccessToken}`);

      if (typeof args === 'string') {
        result = await baseQuery({ url: args, headers: newHeaders }, api, extraOptions);
      } else {
        result = await baseQuery(
          { ...args, headers: { ...args.headers, authorization: `Bearer ${newAccessToken}` } },
          api,
          extraOptions,
        );
      }
    }
  }

  if (result.error) {
    const errorMessage =
      result.error.data && typeof result.error.data === 'object' && 'message' in result.error.data
        ? (result.error.data as { message: string }).message
        : 'An error occurred';

    const requestUrl = result.meta?.request.url || '';
    const errorStatus = typeof result.error.status === 'number' ? result.error.status : 0;

    const shouldIgnoreToast = toastIgnoreList.some(
      (exception) => requestUrl.includes(exception.uri) && exception.status.includes(errorStatus),
    );

    if (!shouldIgnoreToast) {
      toast.error((toastProps) => <CustomErrorToast {...toastProps} message={errorMessage} />, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Campaign'],
  endpoints: () => ({}),
});

export type GetCampaignReportResponse = { blob: Blob; contentDisposition: string | null };

baseApi.injectEndpoints({
  endpoints: (build) => ({
    getCampaignReport: build.query<
      GetCampaignReportResponse,
      { campaignId: string; queryParams: string }
    >({
      query: ({ campaignId, queryParams }) => ({
        url: `/v2/campaigns/${campaignId}/reports${queryParams}`,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const contentDisposition = response.headers.get('Content-Disposition');
          return { blob, contentDisposition };
        },
      }),
    }),
  }),
});

type GetCampaignReportArgs = { campaignId: string; queryParams: string };

export const useLazyGetCampaignReportQuery = (
  baseApi as unknown as {
    useLazyGetCampaignReportQuery: () => [
      (arg: GetCampaignReportArgs) => Promise<{
        data?: GetCampaignReportResponse;
        error?: unknown;
        isSuccess: boolean;
        isError: boolean;
      }>,
      { data?: GetCampaignReportResponse; error?: unknown; isSuccess: boolean; isError: boolean },
    ];
  }
).useLazyGetCampaignReportQuery;
